import React, { createContext, useContext, useState, useRef } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';

interface Practice {
  id: number;
  name: string;
  words: string[];
  createdAt: string;
}

interface PracticeContextType {
  practices: Practice[];
  practiceResults: { [key: number]: { correct: number; incorrect: number } };
  practiceHistory: { [key: number]: { lastPracticed: string | null; timesPracticed: number } };
  addPractice: (name: string, words: string[]) => void;
  updatePractice: (id: number, name: string, words: string[]) => void;
  deletePractice: (id: number) => void;
  updatePracticeHistory: (practiceId: number) => void;
  updatePracticeResults: (practiceId: number, isCorrect: boolean) => void;
  getPracticeById: (id: number) => Practice | null;
  initializeSampleData: () => void;
  importSharedList: (name: string, words: string[]) => void;
  currentPractice: any;
  setCurrentPractice: React.Dispatch<React.SetStateAction<any>>;
  currentWord: string;
  setCurrentWord: React.Dispatch<React.SetStateAction<string>>;
  userInput: string[];
  setUserInput: React.Dispatch<React.SetStateAction<string[]>>;
  inputRefs: React.MutableRefObject<any[]>;
  setInputRefs: React.Dispatch<React.SetStateAction<React.MutableRefObject<any[]>>>;
  showKeyboard: boolean;
  setShowKeyboard: React.Dispatch<React.SetStateAction<boolean>>;
  activeInputIndex: number | null;
  setActiveInputIndex: React.Dispatch<React.SetStateAction<number | null>>;
  feedback: { visible: boolean; correct: boolean; correctWord: string };
  setFeedback: React.Dispatch<React.SetStateAction<{ visible: boolean; correct: boolean; correctWord: string }>>;
  usedWords: string[];
  setUsedWords: React.Dispatch<React.SetStateAction<string[]>>;
  correctCount: number;
  setCorrectCount: React.Dispatch<React.SetStateAction<number>>;
  incorrectCount: number;
  setIncorrectCount: React.Dispatch<React.SetStateAction<number>>;
  showResults: boolean;
  setShowResults: React.Dispatch<React.SetStateAction<boolean>>;
  newPracticeName: string;
  setNewPracticeName: React.Dispatch<React.SetStateAction<string>>;
  newPracticeWords: string;
  setNewPracticeWords: React.Dispatch<React.SetStateAction<string>>;
  finalResults: { correct: number; incorrect: number };
  setFinalResults: React.Dispatch<React.SetStateAction<{ correct: number; incorrect: number }>>;
  editingPractice: Practice | null;
  setEditingPractice: React.Dispatch<React.SetStateAction<Practice | null>>;
  isPronouncing: boolean;
  setIsPronouncing: React.Dispatch<React.SetStateAction<boolean>>;
  pronunciationStatus: string | null;
  setPronunciationStatus: React.Dispatch<React.SetStateAction<string | null>>;
  showForm: boolean;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create the context
const PracticeContext = createContext<PracticeContextType | undefined>(undefined);

/**
 * Provider component for practice data
 * @param {Object} props - Component props
 * @returns {JSX.Element} Provider component
 */
export const PracticeProvider = ({ children }: { children: React.ReactNode }) => {
  // Practice lists
  const [practices, setPractices] = useLocalStorage<Practice[]>('practices', []);
  
  // Practice results
  const [practiceResults, setPracticeResults] = useLocalStorage<{ [key: number]: { correct: number; incorrect: number } }>('practiceResults', {});
  
  // Practice history
  const [practiceHistory, setPracticeHistory] = useLocalStorage<{ [key: number]: { lastPracticed: string | null; timesPracticed: number } }>('practiceHistory', {});

  // Create refs outside of the state to prevent infinite loops
  const inputRefsRef = useRef<any[]>([]);
  
  // Initialize state without the ref inside it to prevent circular dependencies
  const [state, setState] = useState({
    currentPractice: null,
    currentWord: '',
    userInput: [''],
    // Only show keyboard in practice mode, not in form mode
    showKeyboard: false,
    activeInputIndex: null as number | null,
    feedback: { visible: false, correct: false, correctWord: '' },
    usedWords: [''],
    correctCount: 0,
    incorrectCount: 0,
    showResults: false,
    newPracticeName: '',
    newPracticeWords: '',
    finalResults: { correct: 0, incorrect: 0 },
    editingPractice: null as Practice | null,
    isPronouncing: false,
    pronunciationStatus: null as string | null,
    showForm: false
  });

  // Use functional updates to prevent stale state references
  // This is a critical fix to prevent infinite update loops
  // Updated to use the external inputRefsRef instead of state.inputRefs
  const setInputRefs = (value: React.SetStateAction<React.MutableRefObject<any[]>>) => {
    // Since inputRefsRef is a ref, we can modify it directly without setState
    // This breaks the circular dependency that was causing infinite loops
    if (typeof value === 'function') {
      // This is a no-op since we're not using functional updates with refs
      // But we keep it for API compatibility
    } else {
      inputRefsRef.current = value.current;
    }
  };

  // Add a new practice list
  const addPractice = (name: string, words: string[]) => {
    const id = Date.now();
    const newPractice: Practice = {
      id,
      name,
      words,
      createdAt: new Date().toISOString()
    };
    
    setPractices([...practices, newPractice]);
  };
  
  // Import a shared list with notification
  const importSharedList = (name: string, words: string[]) => {
    // Add the practice list
    addPractice(name, words);
    
    // Show a notification or perform any additional actions needed for imports
    // This function allows for future expansion of import functionality
  };
  
  // Update an existing practice list
  const updatePractice = (id: number, name: string, words: string[]) => {
    const updatedPractices = practices.map(practice => {
      if (practice.id === id) {
        return {
          ...practice,
          name,
          words,
          updatedAt: new Date().toISOString()
        };
      }
      return practice;
    });
    
    setPractices(updatedPractices);
  };
  
  // Delete a practice list
  const deletePractice = (id: number) => {
    setPractices(practices.filter(practice => practice.id !== id));
    
    // Also clean up related data
    const newResults = { ...practiceResults };
    delete newResults[id];
    setPracticeResults(newResults);
    
    const newHistory = { ...practiceHistory };
    delete newHistory[id];
    setPracticeHistory(newHistory);
  };
  
  // Update practice history
  const updatePracticeHistory = (practiceId: number) => {
    const newHistory = { ...practiceHistory };
    
    if (!newHistory[practiceId]) {
      newHistory[practiceId] = {
        lastPracticed: null,
        timesPracticed: 0
      };
    }
    
    newHistory[practiceId].lastPracticed = new Date().toISOString();
    newHistory[practiceId].timesPracticed += 1;
    
    setPracticeHistory(newHistory);
  };
  
  // Update practice results
  const updatePracticeResults = (practiceId: number, isCorrect: boolean) => {
    const newResults = { ...practiceResults };
    
    if (!newResults[practiceId]) {
      newResults[practiceId] = {
        correct: 0,
        incorrect: 0
      };
    }
    
    if (isCorrect) {
      newResults[practiceId].correct += 1;
    } else {
      newResults[practiceId].incorrect += 1;
    }
    
    setPracticeResults(newResults);
  };
  
  // Get a practice by ID
  const getPracticeById = (id: number) => {
    return practices.find(practice => practice.id === id) || null;
  };
  
  // Initialize with sample data if empty
  const initializeSampleData = () => {
    if (practices.length === 0) {
      const currentDate = new Date().toISOString();
      const sampleData: Practice[] = [
        { 
          id: 1, 
          name: 'Common Words', 
          words: ['apple', 'banana', 'computer', 'elephant', 'furniture'], 
          createdAt: currentDate 
        },
        { 
          id: 2, 
          name: 'Science Terms', 
          words: ['hypothesis', 'experiment', 'molecule', 'biology', 'chemistry'], 
          createdAt: new Date(Date.now() - 86400000).toISOString() 
        }
      ];
      setPractices(sampleData);
    }
  };
  
  // Value to be provided by the context
  const value: PracticeContextType = {
    practices,
    practiceResults,
    practiceHistory,
    addPractice,
    updatePractice,
    deletePractice,
    updatePracticeHistory,
    updatePracticeResults,
    getPracticeById,
    initializeSampleData,
    importSharedList,
    currentPractice: state.currentPractice,
    setCurrentPractice: (value: React.SetStateAction<any>) => {
      const currentPractice = typeof value === 'function' ? value(state.currentPractice) : value;
      setState(prevState => ({ ...prevState, currentPractice }));
    },
    currentWord: state.currentWord,
    setCurrentWord: (value: React.SetStateAction<string>) => {
      const currentWord = typeof value === 'function' ? value(state.currentWord) : value;
      setState(prevState => ({ ...prevState, currentWord }));
    },
    userInput: state.userInput,
    setUserInput: (value: React.SetStateAction<string[]>) => {
      const userInput = typeof value === 'function' ? value(state.userInput) : value;
      setState(prevState => ({ ...prevState, userInput }));
    },
    inputRefs: inputRefsRef,
    setInputRefs,
    showKeyboard: state.showKeyboard,
    setShowKeyboard: (value: React.SetStateAction<boolean>) => {
      const showKeyboard = typeof value === 'function' ? value(state.showKeyboard) : value;
      setState(prevState => ({ ...prevState, showKeyboard }));
    },
    activeInputIndex: state.activeInputIndex,
    setActiveInputIndex: (value: React.SetStateAction<number | null>) => {
      const activeInputIndex = typeof value === 'function' ? value(state.activeInputIndex) : value;
      setState(prevState => ({ ...prevState, activeInputIndex }));
    },
    feedback: state.feedback,
    setFeedback: (value: React.SetStateAction<{ visible: boolean; correct: boolean; correctWord: string }>) => {
      const feedback = typeof value === 'function' ? value(state.feedback) : value;
      setState(prevState => ({ ...prevState, feedback }));
    },
    usedWords: state.usedWords,
    setUsedWords: (value: React.SetStateAction<string[]>) => {
      const usedWords = typeof value === 'function' ? value(state.usedWords) : value;
      setState(prevState => ({ ...prevState, usedWords }));
    },
    correctCount: state.correctCount,
    setCorrectCount: (value: React.SetStateAction<number>) => {
      const correctCount = typeof value === 'function' ? value(state.correctCount) : value;
      setState(prevState => ({ ...prevState, correctCount }));
    },
    incorrectCount: state.incorrectCount,
    setIncorrectCount: (value: React.SetStateAction<number>) => {
      const incorrectCount = typeof value === 'function' ? value(state.incorrectCount) : value;
      setState(prevState => ({ ...prevState, incorrectCount }));
    },
    showResults: state.showResults,
    setShowResults: (value: React.SetStateAction<boolean>) => {
      const showResults = typeof value === 'function' ? value(state.showResults) : value;
      setState(prevState => ({ ...prevState, showResults }));
    },
    newPracticeName: state.newPracticeName,
    setNewPracticeName: (value: React.SetStateAction<string>) => {
      const newPracticeName = typeof value === 'function' ? value(state.newPracticeName) : value;
      setState(prevState => ({ ...prevState, newPracticeName }));
    },
    newPracticeWords: state.newPracticeWords,
    setNewPracticeWords: (value: React.SetStateAction<string>) => {
      const newPracticeWords = typeof value === 'function' ? value(state.newPracticeWords) : value;
      setState(prevState => ({ ...prevState, newPracticeWords }));
    },
    finalResults: state.finalResults,
    setFinalResults: (value: React.SetStateAction<{ correct: number; incorrect: number }>) => {
      const finalResults = typeof value === 'function' ? value(state.finalResults) : value;
      setState(prevState => ({ ...prevState, finalResults }));
    },
    editingPractice: state.editingPractice,
    setEditingPractice: (value: React.SetStateAction<Practice | null>) => {
      const editingPractice = typeof value === 'function' ? value(state.editingPractice) : value;
      setState(prevState => ({ ...prevState, editingPractice }));
    },
    isPronouncing: state.isPronouncing,
    setIsPronouncing: (value: React.SetStateAction<boolean>) => {
      const isPronouncing = typeof value === 'function' ? value(state.isPronouncing) : value;
      setState(prevState => ({ ...prevState, isPronouncing }));
    },
    pronunciationStatus: state.pronunciationStatus,
    setPronunciationStatus: (value: React.SetStateAction<string | null>) => {
      const pronunciationStatus = typeof value === 'function' ? value(state.pronunciationStatus) : value;
      setState(prevState => ({ ...prevState, pronunciationStatus }));
    },
    showForm: state.showForm,
    setShowForm: (value: React.SetStateAction<boolean>) => {
      // Use a functional update to prevent stale state references
      setState(prevState => {
        // Calculate the new showForm value using the functional update pattern
        const newShowForm = typeof value === 'function' ? value(prevState.showForm) : value;
        
        // Only update if the value is actually changing to prevent unnecessary renders
        if (newShowForm === prevState.showForm) {
          return prevState; // Return the same state object to prevent re-renders
        }
        
        // When showing the form, explicitly set showKeyboard to false
        // This prevents the virtual keyboard from interfering with form focus
        if (newShowForm) {
          return { 
            ...prevState, 
            showForm: newShowForm, 
            showKeyboard: false,  // Hide keyboard in form view
            activeInputIndex: null
          };
        } else {
          // When returning to practice view, ensure keyboard is visible
          return { 
            ...prevState, 
            showForm: newShowForm,
            showKeyboard: true  // Always show keyboard in practice view
          };
        }
      });
    }
  };
  
  // We don't need to memoize the value as it would create a circular dependency
  // Instead, we've fixed the state update functions to prevent infinite loops
  return (
    <PracticeContext.Provider value={value}>
      {children}
    </PracticeContext.Provider>
  );
};

/**
 * Custom hook to use the practice context
 * @returns {Object} Practice context value
 */
export const usePractice = () => {
  const context = useContext(PracticeContext);
  
  if (context === undefined) {
    throw new Error('usePractice must be used within a PracticeProvider');
  }
  
  return context;
};

export default PracticeContext;
